import type {Component} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

interface StepButtonsProps {
  property: string;
  propertyLabel: string;
  type: string;
  step: number;
  min: number;
  max: number;
  units: string;
  component: Component;
  isResponsive?: boolean;
  defaults?: number | string;
}

interface StepButtonsEmitData {
  el: HTMLElement;
  propertyName: string;
  value: number;
}

interface CustomPropertyHandlerOptions {
  property: string;
  value: number;
}

function safeQuerySelector(parent: Element | Document, selector: string): Element | null {
  return parent.querySelector(selector);
}

function assertElement<T extends Element>(element: Element | null, selector: string): T {
  if (!element) {
    throw new Error(`Required element not found: ${selector}`);
  }
  return element as T;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_STEP_BUTTONS, {
    create(data: any) {
      // Extract props safely from the GrapesJS data structure
      const grapesjsProps = data.props ?? {};
      const props: StepButtonsProps = {
        property: (grapesjsProps.property as string) ?? '',
        propertyLabel: (grapesjsProps.propertyLabel as string) ?? '',
        type: (grapesjsProps.type as string) ?? '',
        step: (grapesjsProps.step as number) ?? 1,
        min: (grapesjsProps.min as number) ?? 0,
        max: (grapesjsProps.max as number) ?? 100,
        units: (grapesjsProps.units as string) ?? '',
        component: (grapesjsProps.component as Component) ?? editor.getSelected(),
        isResponsive: grapesjsProps.isResponsive as boolean,
        defaults: grapesjsProps.defaults as number | string,
      };

      const change = data.change;
      const el = document.createElement('div');
      el.className = props.type;

      el.innerHTML = getStepButtonsHTML(props);
      
      const desktopEl = assertElement<HTMLElement>(safeQuerySelector(el, '.js-container-desktop'), '.js-container-desktop');

      function attachHandlers(containerEl: HTMLElement): void {
        const inputTextEl = assertElement<HTMLInputElement>(safeQuerySelector(containerEl, '.js-step-input'), '.js-step-input');
        setStepButtonsHandlers(inputTextEl, props, change, containerEl);
        setStepButtonsInitialValue(containerEl, props);
      }

      attachHandlers(desktopEl);

      if (props.isResponsive) {
        const propertyName = props.property;
        props.property = window.PMW.emails.MOBILE_CSS_PROP_PREFIX + propertyName;
        const mobileEl = assertElement<HTMLElement>(safeQuerySelector(el, '.js-container-mobile'), '.js-container-mobile');
        attachHandlers(mobileEl);

        props.property = window.PMW.emails.TABLET_CSS_PROP_PREFIX + propertyName;
        const tabletEl = assertElement<HTMLElement>(safeQuerySelector(el, '.js-container-tablet'), '.js-container-tablet');
        attachHandlers(tabletEl);

        props.property = propertyName;
      }

      return el;
    },

    emit(data: any, emitData: StepButtonsEmitData): void {
      const grapesjsProps = data.props ?? {};
      const props: StepButtonsProps = {
        property: (grapesjsProps.property as string) ?? '',
        propertyLabel: (grapesjsProps.propertyLabel as string) ?? '',
        type: (grapesjsProps.type as string) ?? '',
        step: (grapesjsProps.step as number) ?? 1,
        min: (grapesjsProps.min as number) ?? 0,
        max: (grapesjsProps.max as number) ?? 100,
        units: (grapesjsProps.units as string) ?? '',
        component: (grapesjsProps.component as Component) ?? editor.getSelected(),
        isResponsive: grapesjsProps.isResponsive as boolean,
        defaults: grapesjsProps.defaults as number | string,
      };

      const {el, propertyName, value} = emitData;
      
      let property = props.property;

      if (propertyName) {
        property = propertyName;
      }

      if (!property) {
        return;
      }

      const valueString = value.toString() + props.units;
      const tabletCssPropRegex = new RegExp(`^${window.PMW.emails.TABLET_CSS_PROP_PREFIX}`);
      const mobileCssPropRegex = new RegExp(`^${window.PMW.emails.MOBILE_CSS_PROP_PREFIX}`);
      const isTabletPropUpdate = props.isResponsive && tabletCssPropRegex.test(property);
      const isMobilePropUpdate = props.isResponsive && mobileCssPropRegex.test(property);

      handleCustomProperty({property, value});
      
      if (isTabletPropUpdate) {
        editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
          property,
          value: valueString,
          id: props.component.ccid,
          deviceID: window.PMW.emails.DEVICE_TABLET,
          cssPrefix: window.PMW.emails.TABLET_CSS_PROP_PREFIX,
        });
      } else if (isMobilePropUpdate) {
        editor.runCommand(window.PMW.emails.commands.SET_DEVICE_MEDIA_QUERY, {
          property,
          value: valueString,
          id: props.component.ccid,
          deviceID: window.PMW.emails.DEVICE_MOBILE,
          cssPrefix: window.PMW.emails.MOBILE_CSS_PROP_PREFIX,
        });
      }

      editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
        property, 
        value: valueString, 
        component: props.component
      });
      
      updateStepButtonsState(el);
    },
  });

  function handleCustomProperty({property, value}: CustomPropertyHandlerOptions): void {
    if (property === 'pmw-columns') {
      editor.runCommand(window.PMW.emails.commands.UPDATE_TABLE_COLUMNS, {sliderVal: value});
    }

    if (property === 'pmw-rows') {
      editor.runCommand(window.PMW.emails.commands.UPDATE_TABLE_ROWS, {sliderVal: value});
    }
  }

  function setStepButtonsHandlers(
    inputEl: HTMLInputElement, 
    props: StepButtonsProps, 
    change: (...args: any[]) => void,
    el: HTMLElement
  ): void {
    const propertyName = props.property;
    const stepDownBtn = assertElement<HTMLButtonElement>(safeQuerySelector(el, '.js-step-down-btn'), '.js-step-down-btn');
    const stepUpBtn = assertElement<HTMLButtonElement>(safeQuerySelector(el, '.js-step-up-btn'), '.js-step-up-btn');
    const inputBtnContainer = assertElement<HTMLElement>(safeQuerySelector(el, '.pmw-input-btns-container'), '.pmw-input-btns-container');

    function handleBtnClick(deltaValue: number): void {
      const inputElement = assertElement<HTMLInputElement>(safeQuerySelector(el, '.js-step-input'), '.js-step-input');
      const currVal = parseInt(inputElement.value);
      inputElement.value = (currVal + deltaValue).toString();
      change({el, propertyName, value: currVal + deltaValue});
    }

    stepDownBtn.addEventListener('click', () => {
      handleBtnClick(-1);
    });
    stepUpBtn.addEventListener('click', () => {
      handleBtnClick(1);
    });

    inputEl.addEventListener('focus', () => {
      inputBtnContainer.classList.add('-selected');
    });

    inputEl.addEventListener('blur', () => {
      inputBtnContainer.classList.remove('-selected');
    });

    inputEl.addEventListener('change', () => {
      const value = getValidatedPropertyValue(inputEl);
      change({el, propertyName, value});
    });
  }

  function getValidatedPropertyValue(el: HTMLInputElement): number {
    const value = parseInt(el.value);
    const min = parseInt(el.getAttribute('min') ?? '0');
    const max = parseInt(el.getAttribute('max') ?? '100');

    if (value < min) {
      el.value = min.toString();
      return min;
    } 
    
    if (value > max) {
      el.value = max.toString();
      return max;
    }

    return value;
  }

  function getStepButtonsHTML(props: StepButtonsProps): string {
    let tabletContainer = '';
    let mobileContainer = '';
    const propertyInputSelectors = '';
    let desktopContainerClassName = 'js-container-desktop';
    const tabletContainerClassName = 'hidden-device-desktop hidden-device-mobile js-container-tablet';
    const mobileContainerClassName = 'hidden-device-desktop hidden-device-tablet js-container-mobile';

    desktopContainerClassName = (props.isResponsive ? 'hidden-device-tablet hidden-device-mobile ' : '') + desktopContainerClassName;

    function getStepButtonsContainerMarkup(stepProps: StepButtonsProps, responsiveTag = '', className = ''): string {
      return `<div class="pmw-email-editor-step-buttons-container ${className}">
          <div class="pmw-email-editor-step-buttons-label-container">
              <span class="style-manager-label"> 
                  ${(responsiveTag ? `<span class="highlight-tag body-sm-bold -rounded">${responsiveTag}</span>` : '') + stepProps.propertyLabel}
              </span>
              <div class="pmw-input-btns-container">
                  <button class="btn step-btn -down icon-minus size-icon-16 js-step-down-btn"></button>
                  <div class="pmw-input-container">
                      <input data-units="${stepProps.units}" class="pmw-step-buttons-text content-body body-md-regular js-step-input js-emails-${stepProps.property}-input" type="number" min="${stepProps.min}" max="${stepProps.max}" step="${stepProps.step}"/>
                      <span class="pmw-email-editor-step-buttons-units ${stepProps.units ? '' : '_hidden'}">${stepProps.units}</span>
                  </div>
                  <button class="btn step-btn -up icon-plus size-icon-16 js-step-up-btn"></button>
              </div>
          </div>
          ${propertyInputSelectors} 
      </div>`;
    }

    const desktopContainer = getStepButtonsContainerMarkup(props, '', desktopContainerClassName);

    if (props.isResponsive) {
      const propertyName = props.property;
      props.property = window.PMW.emails.MOBILE_CSS_PROP_PREFIX + propertyName;
      mobileContainer = getStepButtonsContainerMarkup(props, 'Mobile', mobileContainerClassName);
      props.property = window.PMW.emails.TABLET_CSS_PROP_PREFIX + propertyName;
      tabletContainer = getStepButtonsContainerMarkup(props, 'Tablet', tabletContainerClassName);
      props.property = propertyName;
    }

    return `<div class="pmw-email-editor-step-buttons-containers">
        ${desktopContainer}
        ${mobileContainer}
        ${tabletContainer}
    </div>`;
  }

  function updateStepButtonsState(el: HTMLElement): void {
    const stepDownBtn = assertElement<HTMLButtonElement>(safeQuerySelector(el, '.js-step-down-btn'), '.js-step-down-btn');
    const stepUpBtn = assertElement<HTMLButtonElement>(safeQuerySelector(el, '.js-step-up-btn'), '.js-step-up-btn');
    const inputEl = assertElement<HTMLInputElement>(safeQuerySelector(el, '.js-step-input'), '.js-step-input');
    const min = parseInt(inputEl.getAttribute('min') ?? '0');
    const max = parseInt(inputEl.getAttribute('max') ?? '100');
    const val = parseInt(inputEl.value);

    if (val <= min) {
      stepDownBtn.setAttribute('disabled', 'true');
    } else if (val >= max) {
      stepUpBtn.setAttribute('disabled', 'true');
    } else {
      stepDownBtn.removeAttribute('disabled');
      stepUpBtn.removeAttribute('disabled');
    }
  }

  function setStepButtonsInitialValue(el: HTMLElement, props: StepButtonsProps): void {
    const value = editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_STYLE, {
      component: props.component, 
      property: props.property
    }) as string | null;

    const inputEl = assertElement<HTMLInputElement>(safeQuerySelector(el, '.js-step-input'), '.js-step-input');
    const defaultValue = props.defaults?.toString() ?? '0';
    const parsedValue = value !== null ? parseInt(value, 10) : parseInt(defaultValue, 10);

    const finalValue = isNaN(parsedValue) ? parseInt(defaultValue, 10) : parsedValue;
    inputEl.value = finalValue.toString();

    if (!isNaN(finalValue)) {
      updateStepButtonsState(el);
    }
  }
}